"use client";

import React from "react";
import TrackedLink from "@components/TrackedLink";
import ScheduleCall from "@components/ScheduleCall";
import { track } from "@lib/track";

const WHAT_YOU_GET = [
  { label: "Lab results review", detail: "We go through your results together — what they mean in plain language and what actually matters for your situation" },
  { label: "Key takeaways", detail: "The 2–3 most important signals from your results and what your body is telling you" },
  { label: "Clear next steps", detail: "Specific, prioritised actions — not a long list of things to research yourself" },
  { label: "Protocol adjustment", detail: "If you're already following a plan, we review what's working and what needs to change" },
];

export default function FollowUpClient() {
  const onPlay = (label: string) => {
    try {
      track(
        "video_play",
        { label, component: "FlexiConsulPage.Video" },
        { event: "video_play", label, component: "FlexiConsultPage.Video" }
      );
    } catch {}
  };

  const scrollToScheduler: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    document.getElementById("schedule-call")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="relative bg-[var(--color-brand)] text-[var(--text-on-dark)]">

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <header className="mx-auto max-w-5xl px-5 md:px-10 py-12 md:py-20 text-center">
        <p className="text-xs md:text-sm uppercase tracking-widest opacity-70 mb-4 font-semibold">
          30-Minute Follow-up — Niki Charnock
        </p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight text-balance">
          Got Results Back. Not Sure What to Do With Them?
        </h1>
        <p className="mt-5 text-sm md:text-lg opacity-90 max-w-3xl mx-auto leading-relaxed text-pretty">
          A focused 30-minute session with{" "}
          <strong>Niki Charnock, Naturopathic Nutritionist</strong> — to review your lab results,
          make sense of what they mean, and map out your next steps clearly.
        </p>
        <div className="mt-8">
          <TrackedLink
            href="#schedule-call"
            onClick={scrollToScheduler}
            variant="solid"
            size="lg"
            color="base"
            event="cta_click"
            label="Flexible Consult | Book Hero"
            component="FollowUpPage"
          >
            Book Your Follow-up
          </TrackedLink>
        </div>
        <p className="mt-3 text-xs md:text-sm opacity-70">£80 · 30 minutes · Online</p>
      </header>

      {/* ── BODY ─────────────────────────────────────────────────────────────── */}
      <main className="mx-auto max-w-5xl -mt-6 rounded-t-3xl bg-[var(--bg-surface)] px-5 md:px-10 pb-20 shadow-2xl text-[var(--text-on-light)]">

        {/* ── VIDEO ────────────────────────────────────────────────────────── */}
        <section className="pt-10 md:pt-14 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)]">
            What You'll Get in 30 Minutes
          </h2>
          <p className="mt-2 text-[var(--text-on-light-muted)] max-w-md mx-auto text-sm md:text-base">
            A focused review of your results, key takeaways, and simple next steps.
          </p>
          <div className="mt-6">
            <video
              controls
              poster="/images/Niki_Charnock.webp"
              className="mx-auto w-full md:w-2/4 max-w-2xl rounded-2xl shadow-lg"
              onPlay={() => onPlay("Follow-up Overview")}
            >
              <source src="/images/followUpCall.mp4" type="video/mp4" />
            </video>
          </div>
        </section>

        {/* ── WHAT YOU GET ─────────────────────────────────────────────────── */}
        <section className="mt-14 max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)] text-center mb-8">
            What's Included
          </h2>
          <div className="space-y-3">
            {WHAT_YOU_GET.map((item, i) => (
              <div key={i} className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-4 shadow-sm flex gap-4 items-start">
                <span className="mt-0.5 text-green-600 font-bold shrink-0">✓</span>
                <div>
                  <p className="font-semibold text-sm text-[var(--text-brand)]">{item.label}</p>
                  <p className="text-sm text-[var(--text-on-light-muted)] mt-0.5">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── PRICING + SCHEDULER ──────────────────────────────────────────── */}
        <section id="schedule-call" className="mt-14 max-w-3xl mx-auto scroll-mt-24">
          <div className="rounded-2xl border-2 border-[var(--text-brand)] bg-[var(--bg-card)] p-6 shadow-md text-center mb-8">
            <p className="text-sm text-[var(--text-on-light-muted)] uppercase tracking-wider font-semibold mb-1">Investment</p>
            <p className="text-4xl font-extrabold text-[var(--text-brand)]">£80</p>
            <p className="text-sm text-[var(--text-on-light-muted)] mt-1">30 minutes · Online</p>
          </div>

          <ScheduleCall
            duration={30}
            meetingType="flexible"
            title="Follow-up Consultation"
            subtitle="Choose a time that works for you. We'll interpret your results together and map your next steps."
            ctaText="Confirm & Book"
            componentName="FollowUpPage"
          />
        </section>

        {/* ── CROSS-SELL ───────────────────────────────────────────────────── */}
        <section className="mt-10 max-w-3xl mx-auto rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-6 text-center shadow-sm">
          <h2 className="text-base md:text-lg font-semibold text-[var(--text-brand)]">Need a deeper dive?</h2>
          <p className="text-sm text-[var(--text-on-light-muted)] mt-2 mb-5 max-w-sm mx-auto">
            For a full root-cause assessment and personalised plan — nutrition, supplements,
            lifestyle and functional testing — book an initial consultation instead.
          </p>
          <TrackedLink
            href="/workwithme/consultation"
            variant="solid"
            size="md"
            color="base"
            event="cta_click"
            label="Flexible Consult | View Consultation"
            component="FollowUpPage"
          >
            View Initial Consultation →
          </TrackedLink>
        </section>

        <img
          src="/images/Niki_sig.webp"
          alt="Signature — Niki Charnock"
          className="mt-10 mx-auto"
          style={{ width: 150 }}
        />
      </main>

      {/* ── STICKY MOBILE CTA ────────────────────────────────────────────────── */}
      <div className="fixed inset-x-0 bottom-0 z-40 md:hidden border-t border-[var(--border-on-light)] bg-[var(--bg-surface)]/95 backdrop-blur supports-[backdrop-filter]:bg-[var(--bg-surface)]/70">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3">
          <span className="text-sm font-medium text-[var(--text-on-light)]">Ready to continue?</span>
          <TrackedLink
            href="#schedule-call"
            onClick={scrollToScheduler}
            variant="solid"
            size="md"
            color="base"
            event="cta_click"
            label="Flexible Consult | Book Sticky"
            component="FollowUpPage"
          >
            Book now
          </TrackedLink>
        </div>
      </div>
    </div>
  );
}
